import {isAdmin, methodToActionPermissions} from './auth-helper';
import {ALIASES, RESOURCES} from './definitions';
import {
    AliasValidator, DefaultValidator, PageValidator, ResourceValidator,
} from './validators';

/**
 * Class Auth (Authorization).
 * The purpose of this class is to be a Singleton (see default export)
 * the instance should provide access to all possible validations as well as
 * helper methods. Examples:
 *
 * // check if the user is admin.
 * // auth.$isAdmin
 * // check if the transactions resource can be access with no restrictions (full access [all]).
 * // auth.transactions.isAllowed
 * // check if the customers resource can be access using add action
 * // auth.customers.add.isAllowed
 * // check if the profiles resource can be access for add and read action
 * // auth.profiles.read.delete.isAllowed
 * // check if the users page can be access
 * // auth.$pages.users.isAllowed
 *
 * @extends DefaultValidator
 */
class Auth extends DefaultValidator {
    constructor() {
        super();
        this.$init();
    }

    /**
     * Reset the instance to its initial state
     * */
    $init() {
        this.$isAdmin = false;
        this.$permissions = {};
        this.$destroyValidators();
    }

    /**
     * Load user permissions
     *
     * // Method Permissions
     * // [{resourceName: 'resource' , methods:['GET', 'PUT']}]
     * // Saves as Action Permissions
     * // {resource: ['add', 'delete', 'read']}
     *
     * @param {Array} methodPermissions
     * */
    $loadPermissions(methodPermissions) {
        this.$init();
        this.$isAdmin = isAdmin(methodPermissions);
        if (!this.$isAdmin) {
            this.$permissions = methodToActionPermissions(methodPermissions);
        }
    }

    /**
     * Route the requested property to the right validator
     * This is the first actions executed when an authorization is unknown
     * It will catch the validator for following request.
     *
     * // In this case if profile is unknown this function will be executed.
     * // auth.profile.isAllowed
     * @param {string} unknownProperty
     * @return {AliasValidator | ResourceValidator | PageValidator | DefaultValidator}
     */
    $validate(unknownProperty) {
        if (!this.$isAdmin) {
            if (ALIASES[unknownProperty]) {
                return this.$addValidator(unknownProperty,
                    AliasValidator, {$alias: ALIASES[unknownProperty]});
            }
            if (RESOURCES[unknownProperty]) {
                return this.$addValidator(unknownProperty, ResourceValidator);
            }
            if (unknownProperty === '$pages') {
                return this.$addValidator(unknownProperty, PageValidator);
            }
            if (process.env.NODE_ENV !== 'production') {
                console.warn(`Authorization: undefined property ${unknownProperty}`);
            }
        }
        // If no validators exists to process this authorization
        // A default validator will be created so that it will be allowed
        // based on the $isAdmin state.
        // admin + unknown validator === allowed. otherwise denied!
        return this.$addValidator(unknownProperty, DefaultValidator);
    }

    get isAllowed() {
        return this.$isAdmin;
    }
}

export default new Auth();
